import React, { useMemo, useState } from 'react'
import { Button, RadioInput, TextInput, Table } from '@demandlocal/components'
import { createColumnHelper } from '@tanstack/react-table'
import { useGetDealerships } from '../../../api/admin/useGetDealerships'
import { ReactComponent as ActionDelete } from '../../../images/icons/action_delete.svg'
import { ReactComponent as ActionEdit } from '../../../images/icons/action_edit.svg'
import { Dealership } from '../../../lib/types/Dealership'
import FiltersContext from '../../../components/Filters/FiltersContext'
import { createHtmlPortalNode, OutPortal } from 'react-reverse-portal'
import { useDeleteDealershipById } from '../../../api/admin/useDeleteDealershipById'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router'
import { Outlet } from 'react-router-dom'
import { queryClient } from '../../../api/queryClient'

const columnHelper = createColumnHelper<Dealership>()

const AdminDealerships = (): JSX.Element => {
  const [filters, setFilters] = useState<Record<string, unknown>>({})
  const filtersPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const { mutateAsync: deleteDealershipAsync } = useDeleteDealershipById()
  const navigate = useNavigate()
  const columns = useMemo(
    () => [
      columnHelper.accessor('dealership', {
        header: () => 'Name',
      }),
      columnHelper.accessor('client_id', {
        header: () => 'Mapped client',
        cell: (info) => {
          if (info.row.original.client_id) {
            return (
              <a
                className={'text-primary-persian-blue font-bold'}
                href={`https://admin.demandlocal.net/dashboard/new/customers/clients/${info.row.original.client_id?.value}/overview`}
                target={'_blank'}
                rel="noreferrer">
                {info.row.original.client_id?.label}
              </a>
            )
          }
          return null
        },
      }),
      columnHelper.accessor('address', {
        header: () => 'Address',
      }),
      columnHelper.accessor('city', {
        header: () => 'City',
      }),
      columnHelper.accessor('state', {
        header: () => 'State',
      }),
      columnHelper.accessor('premium', {
        header: () => 'Premium',
        cell: ({ row }) => (row.original.premium ? 'Yes' : 'No'),
      }),
      columnHelper.display({
        id: 'actions',
        header: () => 'Actions',
        cell: (info) => (
          <div className={'flex flex-row'}>
            <ActionDelete
              className={'cursor-pointer'}
              onClick={async () => {
                if (confirm('Are you sure you want to delete this dealership?')) {
                  const toastId = toast.loading('Deleting dealership...')
                  await deleteDealershipAsync({
                    id: info.row.original._id,
                  })
                  await queryClient.invalidateQueries(['admin', 'dealerships'])
                  toast.success('Dealership deleted successfully', { id: toastId })
                }
              }}
            />
            <ActionEdit
              stroke={'#0932C2'}
              className={'ml-4 cursor-pointer'}
              onClick={() => {
                navigate(`./${info.row.original._id}/edit`)
              }}
            />
          </div>
        ),
      }),
    ],
    [deleteDealershipAsync, navigate],
  )
  return (
    <>
      <div className={'flex flex-1 flex-col pb-8'}>
        <div className={'flex flex-1 flex-col relative'}>
          <div className={'pb-4 flex justify-between'}>
            <div className={'font-bold text-base text-primary-xiketic'}>Dealerships</div>
            <div className={'flex gap-4'}>
              <OutPortal node={filtersPortalNode} />
              <Button
                variant={'primary'}
                onClick={(e) => {
                  e.preventDefault()
                  navigate('./new')
                }}>
                + Add New Dealership
              </Button>
            </div>
          </div>
          <FiltersContext
            portalNode={filtersPortalNode}
            setFilters={setFilters}
            defaultValues={filters}>
            <TextInput
              name={'dealership'}
              placeholder={'Dealership name'}
              label={'Dealership name'}
            />
            <TextInput name={'city'} placeholder={'City'} label={'City'} />
            <TextInput name={'state'} placeholder={'State'} label={'State'} />
            <RadioInput
              name={'is_mapped'}
              label={'Is Mapped'}
              options={[
                {
                  label: 'True',
                  value: 'true',
                },
                {
                  label: 'False',
                  value: 'false',
                },
              ]}
            />
            <RadioInput
              name={'has_billable_services'}
              label={'Has billable services'}
              options={[
                {
                  label: 'True',
                  value: 'true',
                },
                {
                  label: 'False',
                  value: 'false',
                },
              ]}
            />
          </FiltersContext>
          <Table columns={columns} filters={filters} fetchFn={useGetDealerships} />
        </div>
      </div>
      <Outlet />
    </>
  )
}

export default AdminDealerships
